import { ChangeDetectionStrategy, Component, computed, input, OnDestroy, output } from '@angular/core';
import { AreaChartComponent } from './components/area-chart/area-chart.component';
import { CommonModule } from '@angular/common';
import { SelectModule } from 'primeng/select';
import { FormsModule } from '@angular/forms';
import { connect } from 'echarts/core';
import { EChartsInstance } from './models/graphs.types';
import { AsyncState, getData, getError } from '../../shared/models/async-state';
import { LoadingSkeletonComponent } from '../../shared/components/loading-skeleton.component';
import { ErrorStateComponent } from '../../shared/components/error-state.component';
import { GraphSeriesSet } from './models/graphs.types';
import { Period, PERIOD_DISPLAY_LABELS, PERIOD_OPTIONS } from '../../core/constants/periods';
import { EmptyStateComponent } from '../../shared/components/empty-state.component';

@Component({
  selector: 'app-graphs',
  imports: [
    AreaChartComponent,
    CommonModule,
    SelectModule,
    FormsModule,
    LoadingSkeletonComponent,
    ErrorStateComponent,
    EmptyStateComponent,
  ],
  templateUrl: './graphs.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GraphsComponent implements OnDestroy {
  readonly dataState = input.required<AsyncState<GraphSeriesSet>>();
  readonly retry = output<void>();
  readonly selectedPeriod = input<Period>('DAY');
  readonly periodChange = output<Period>();

  private chartInstances: EChartsInstance[] = [];
  readonly GROUP_ID = 'profile-charts';

  periods = PERIOD_OPTIONS;

  readonly data = computed(() => getData(this.dataState()));
  readonly error = computed(() => getError(this.dataState()));

  readonly loadSeries = computed(() => {
    return this.data()?.load ?? [];
  });

  readonly socSeries = computed(() => {
    return this.data()?.soc ?? [];
  });

  readonly powerInputSeries = computed(() => {
    return this.data()?.powerInput ?? [];
  });

  readonly hasSelectedPeriodData = computed(() => {
    const set = this.data();
    return (set?.load?.length ?? 0) > 0 || (set?.soc?.length ?? 0) > 0 || (set?.powerInput?.length ?? 0) > 0;
  });

  readonly selectedPeriodLabel = computed(() => {
    return PERIOD_OPTIONS.find(p => p.value === this.selectedPeriod())?.label ?? this.selectedPeriod();
  });

  readonly selectedPeriodSubtitle = computed(() => {
    return PERIOD_DISPLAY_LABELS[this.selectedPeriod()];
  });

  onPeriodSelect(period: Period): void {
    this.periodChange.emit(period);
  }

  onRetry(): void {
    this.retry.emit();
  }

  onChartReady(chart: EChartsInstance): void {
    this.chartInstances.push(chart);

    if (this.chartInstances.length > 1) {
      connect(this.GROUP_ID);
    }
  }

  ngOnDestroy(): void {
    this.chartInstances.forEach(chart => chart.dispose?.());
  }
}
